import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import { Checkbox, Flex, Form, Select, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import GooglePlaceHelpers from '../../../services/helpers/GooglePlaceHelpers';
import AddressHelpers from '../../../services/helpers/AddressHelpers';
import GooglePlacesSearch from '../../../components/form/address/GooglePlacesSearch';
import AddressFields from '../../../components/form/address/AddressFields';
import TimezoneManager from '../../../services/localization/TimezoneManager';
import useInferredTimezone, { TimezoneInference } from '../../../hooks/utility/useInferredTimezone';
import useRegions from '../../../hooks/data/useRegions';

export default function CompanyLocation({ data, onChange, isDemo }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Modals:Company:AddCompanyModal' });

  const timezones = useMemo(() => TimezoneManager.getOptions(i18n.language), [i18n.language]);

  const onTimezoneInferred = useCallback((timezone) => onChange('timezone', timezone), [onChange]);

  const inference = useInferredTimezone(
    {
      address: data.address,
      city: data.city,
      zipCode: data.zip_code,
      country: data.country,
      state: data.state,
      timezone: data.timezone,
      lat: data.lat,
      lon: data.lon,
    },
    onTimezoneInferred,
  );

  const { regions } = useRegions();

  const onPlaceSelected = (place) => {
    const country = GooglePlaceHelpers.getCountry(place);

    onChange({
      name: place.name.split(',')[0] ?? place.name,
      google_place: place,
      address: GooglePlaceHelpers.getAddress(place, AddressHelpers.addressFormatFor(regions, country)),
      city: GooglePlaceHelpers.getCity(place),
      state: GooglePlaceHelpers.getState(place),
      country,
      zip_code: GooglePlaceHelpers.getZipCode(place),
      // Saves the wizard a geocoding round-trip before it can infer the
      // timezone — and these are the coordinates of the outlet itself rather
      // than of the address string as re-parsed by the geocoder.
      lat: place.geometry?.location?.lat() ?? null,
      lon: place.geometry?.location?.lng() ?? null,
    });
  };

  return (
    <Form layout="vertical" autoComplete="none">
      <h3 className="mb-xs">{t('basic_information.title')}</h3>
      <p className="mb-md text-gray-600">{t('basic_information.description')}</p>
      <Form.Item label={t('form.name.label')} required>
        <GooglePlacesSearch
          value={data.name ?? ''}
          onPlaceSelected={onPlaceSelected}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={t('form.name.placeholder')}
          options={{
            fields: ['name', 'place_id', 'address_components', 'geometry'],
            types: ['bakery', 'bar', 'cafe', 'restaurant', 'food'],
          }}
        />
        {data.google_place && (
          <Space className="mt-xs">
            <a href="#">{t('form.google_place.label', { name: data.google_place.name })}</a>
            <DeleteOutlined className="my-auto cursor-pointer" onClick={() => onChange('google_place', null)} />
          </Space>
        )}
      </Form.Item>
      <AddressFields data={data} onChange={onChange} useGrid />
      {/* Pre-filled from the address, never applied silently: the step cannot be
          completed until this holds a value, so proceeding is the confirmation.
          An outlet created without it inherits the merchant's legal region,
          which is the wrong zone for anywhere in a multi-timezone country. */}
      <Form.Item
        label={t('form.timezone.label')}
        required
        validateStatus={inference === TimezoneInference.Failed && !data.timezone ? 'warning' : undefined}
        help={
          inference === TimezoneInference.Failed && !data.timezone ? t('form.timezone.inference_failed') : undefined
        }
      >
        <Select
          showSearch
          loading={inference === TimezoneInference.Loading}
          value={data.timezone ?? null}
          onChange={(value) => onChange('timezone', value)}
          options={timezones}
          filterOption={(input, option) => TimezoneManager.matchesOption(input, option)}
          filterSort={(optionA, optionB, { searchValue }) =>
            TimezoneManager.compareOptions(optionA, optionB, searchValue)
          }
          placeholder={t('form.timezone.placeholder')}
        />
      </Form.Item>
      {!isDemo && (
        <Flex gap="middle" className="items-center">
          <Checkbox
            checked={data.company_info.is_franchise}
            onChange={(e) => onChange('company_info.is_franchise', e.target.checked)}
            id="is-franchise"
            className="scale-125"
          />
          <label htmlFor="is-franchise" className="my-auto">
            {t('form.is_franchise.label')}
          </label>
        </Flex>
      )}
    </Form>
  );
}
